import { equipmentQueryOptions } from "@/api/Equipment";
import { useQuery } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMemo } from "react";
import { Outlet } from "react-router";
import { DisclosureGroup } from "react-aria-components";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import styles from "./Equipment.module.scss";
import NoResults from "@/components/NoResults/NoResults";
import { Heading } from "@medoma/components/Heading/Heading";
import { SplitPane } from "@/components/SplitPane/SplitPane";
import { EquipmentCategory } from "./EquipmentCategory";

export const Equipment = () => {
  const {
    data: equipment = [],
    isPending,
    isError,
    error,
  } = useQuery(equipmentQueryOptions());
  const { t } = useLingui();

  const columns = useMemo(
    () =>
      [
        { id: "name" as const, name: t`Namn`, isRowHeader: true },
        { id: "comment" as const, name: t`Kommentar` },
        { id: "status" as const, name: t`Status` },
        { id: "patient" as const, name: t`Patient` },
        { id: "allocatedAt" as const, name: t`Utlämnad` },
      ] as const,
    [t],
  );

  return (
    <div className={styles.equipment}>
      <SplitPane panelSizeLocalStorageKey="equipment">
        <div>
          <Heading level="h1" className={styles.pageHeader}>
            <Trans>Utrustning</Trans>
          </Heading>
          {isPending ? (
            <Loading message={t`Hämtar utrustning`} padding={24} />
          ) : isError ? (
            <ErrorMessage
              message={`${t`Kunde inte hämta utrustning.`} ${deducedError(error)}`}
              padding={24}
            />
          ) : equipment.length === 0 ? (
            <NoResults message={t`Ingen utrustning att visa.`} padding={24} />
          ) : (
            <DisclosureGroup
              allowsMultipleExpanded
              className={styles.categories}
            >
              {equipment.map((category) => (
                <EquipmentCategory
                  key={category.id}
                  category={category}
                  columns={columns}
                />
              ))}
            </DisclosureGroup>
          )}
        </div>
        <div className={styles.detailsPanel}>
          <Outlet />
        </div>
      </SplitPane>
    </div>
  );
};
